import { Link } from "react-router-dom";
import { ShoppingCart, Star } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useOrdersPaused } from "@/components/SiteClosedGate";
import { gbp } from "@/lib/api";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const paused = useOrdersPaused();
  const outOfStock = product.stock === 0;

  return (
    <div
      data-testid={`product-card-${product.slug}`}
      className="group bg-card border border-border rounded-sm overflow-hidden flex flex-col hover:border-primary hover:-translate-y-0.5 transition-[transform,border-color] duration-200 animate-fade-up"
      style={{ animationDelay: `${(index % 8) * 50}ms` }}
    >
      <Link to={`/products/${product.slug}`} className="relative block aspect-square overflow-hidden bg-white">
        <img src={product.image} alt={product.name} loading="lazy"
          className={`w-full h-full transition-transform duration-500 group-hover:scale-105 ${product.compact_image && !product.card_cover ? "object-contain p-8" : "object-cover"}${product.rotate ? " rotate-90" : ""}`} />
        {product.bargain && (
          <span className="absolute top-2 left-2 bg-amber-500 text-white text-[10px] font-bold px-2 py-1 rounded-sm tracking-wide" data-testid={`bargain-badge-${product.slug}`}>DAMAGED BOX · NEW</span>
        )}
        {product.special_offer && !product.bargain && !outOfStock && (
          <span className="absolute top-2 left-2 bg-accent text-white text-[10px] font-bold px-2 py-1 rounded-sm tracking-wide">{product.was_price ? "BELOW RRP" : "SPECIAL OFFER"}</span>
        )}
        {product.featured && !product.special_offer && !product.bargain && !outOfStock && (
          <span className="absolute top-2 left-2 bg-primary text-white text-[10px] font-bold px-2 py-1 rounded-sm tracking-wide">FEATURED</span>
        )}
        {outOfStock && (
          <span className="absolute top-2 left-2 bg-secondary text-white text-[10px] font-bold px-2 py-1 rounded-sm" data-testid="card-out-of-stock">OUT OF STOCK{product.stock_note ? ` · ${product.stock_note.toUpperCase()}` : ""}</span>
        )}
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <span className="text-[11px] text-muted-foreground font-mono">{product.sku}</span>
        <Link to={`/products/${product.slug}`} className="mt-1 text-sm font-semibold leading-snug line-clamp-2 hover:text-primary transition-colors">
          {product.name}
        </Link>
        {(() => {
          const meta = product.bargain
            ? `${product.condition}${product.packaging ? ` · ${product.packaging}` : ""}`
            : (product.specs?.Connection || product.specs?.Type || "");
          return meta ? <span className="mt-1 block text-[11px] text-muted-foreground line-clamp-1" data-testid={`card-meta-${product.slug}`}>{meta}</span> : null;
        })()}
        <div className="mt-1.5 text-xs text-muted-foreground h-4">
          {product.review_summary?.count > 0 && (
            <span className="flex items-center gap-1" data-testid={`card-rating-${product.slug}`}>
              <Star className="w-3.5 h-3.5 fill-accent text-accent" />
              <span>{product.review_summary.average.toFixed(1)}</span>
              <span>({product.review_summary.count})</span>
            </span>
          )}
        </div>
        <div className="mt-auto pt-3 flex items-end justify-between">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-display font-bold text-foreground">{gbp(product.price)}</span>
              {product.was_price && <span className="text-xs text-muted-foreground">RRP <span className="line-through">{gbp(product.was_price)}</span></span>}
            </div>
            {product.was_price && product.was_price > product.price && (
              <span className="block text-[11px] font-semibold text-accent" data-testid={`card-save-${product.slug}`}>Save {gbp(product.was_price - product.price)} off RRP</span>
            )}
            {product.special_offer && product.offer_ends && (
              <span className="block text-[11px] font-medium text-muted-foreground" data-testid={`card-offer-ends-${product.slug}`}>Offer ends {product.offer_ends}</span>
            )}
            <span className="block text-[11px] font-semibold text-primary">Free tracked delivery</span>
          </div>
          <button
            data-testid={`add-to-cart-${product.slug}`}
            disabled={outOfStock || paused}
            title={paused ? "Orders are paused" : undefined}
            onClick={() => { addItem(product); toast.success("Added to basket"); }}
            className="grid place-items-center w-10 h-10 bg-primary text-white rounded-sm hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Add to basket"
          >
            <ShoppingCart className="w-4.5 h-4.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
